// collector/previewLeads.js
//
// «المحل مهتم» tapped in an offline supplier preview (supplier-preview-offline
// P2-B, locked answer L-L; design Section 2.E). The tap is written onto that
// shop's copy (`lead` of its `poolit-preview` row) and sent from here, once,
// when our server answers again, in the backend's offline form
// `{organizationId, id, at, phone?}` — idempotent by `id`
// (../backend/app/Http/Controllers/Api/V1/Collectors/SupplierPreviewController.php).
//
// NOT an action of the tap queue (collector/actions.js): every queue entry
// names a line of his day, and a lead has none. It rides the queue's triggers
// instead — `flush()` there calls `sendPendingLeads` after every attempt (a
// tap, the detector's online flip, visibility, pageshow, the 60 s tick, the
// login coming back) — and keeps its own backoff 5 → 10 → 20 → 60 s, which
// also retries it every 60 s while nothing else runs. The DELETE a live
// preview closed with no signal still owes (L-K) goes on the same triggers.
//
// ANSWERS: 200 applied / already_applied → `sent`; our API's 403 (not a
// collector any more) or 422 (not a shop, a bad phone) → `refused`, never
// retried; 401 → pause on the login (collector/session.js) and keep; anything
// else — no answer, a timeout, a 5xx, a 419, a page that is not our JSON —
// keep and back off. One lead at a time, under its own cross-tab lock (the
// Chrome tab and the installed app share this storage); a recording that
// overwrites a row meanwhile can at worst put a sent lead back to pending, and
// the server then answers already_applied — never a second message.
import { shallowRef } from 'vue';
import { BACKOFF_MS, isOurApi } from '@/collector/actions.js';
import { newActionId } from '@/collector/ids.js';
import { EXIT_PENDING_KEY, previewExitPending } from '@/collector/previewReplay.js';
import { previewStore, shopKey } from '@/collector/previewStore.js';
import { authPaused, pauseForAuth } from '@/collector/session.js';
import { exitPreview, noteInterest } from '@/services/supplierPreview.service.js';

const LEADS_LOCK = 'poolit-preview-leads';

/** shopId → the lead's status ('pending' | 'sent' | 'refused') — the day page's marks (L-M). */
export const leadStatuses = shallowRef({});

let sending = null;
let failures = 0;
let nextAttemptAt = 0;
let retryTimer = null;
/** The last read of the phone found no pending lead (a tap here clears it; a page load starts unsure). */
let knownEmpty = false;

function noteStatus(shopId, status) {
  leadStatuses.value = { ...leadStatuses.value, [shopKey(shopId)]: status };
}

/**
 * The offline tap (L-L): the lead onto the shop's copy, a new uuid and the tap
 * time; replaces an earlier one of the same shop. Rejects when the copy is gone.
 */
export async function saveOfflineLead(shopId, phone) {
  const row = await previewStore().get(shopId);
  if (!row) throw new Error(`[preview] no copy of shop ${shopId} to keep the lead on`);
  const lead = { id: newActionId(), phone: phone?.trim() || null, at: new Date().toISOString(), status: 'pending', attempts: 0 };
  await previewStore().put({ ...row, lead });
  knownEmpty = false;
  noteStatus(shopId, 'pending');
  return lead;
}

/** The DELETE a live preview closed with no signal still owes (L-K); the key goes on its answer. Never throws. */
let sendingExit = null;
export function sendPendingExit() {
  if (sendingExit || !previewExitPending()) return sendingExit ?? Promise.resolve(false);
  sendingExit = Promise.resolve()
    .then(() => exitPreview())
    .then(() => {
      try {
        localStorage.removeItem(EXIT_PENDING_KEY);
      } catch {
        /* storage blocked */
      }
      return true;
    })
    .catch(() => false)
    .finally(() => {
      sendingExit = null;
    });
  return sendingExit;
}

/** Write a lead's answer onto its row — only while that row still holds the same lead. */
async function settle(shopId, leadId, patch) {
  const row = await previewStore().get(shopId);
  if (row?.lead?.id !== leadId) return;
  await previewStore().put({ ...row, lead: { ...row.lead, ...patch } });
  if (patch.status) noteStatus(shopId, patch.status);
}

/** 'sent' | 'refused' | 'paused' | 'keep' */
async function sendOne(row) {
  const { lead } = row;
  const attempts = (Number(lead.attempts) || 0) + 1;
  try {
    const body = await noteInterest({ organizationId: row.shopId, id: lead.id, at: lead.at, phone: lead.phone });
    if (body?.ok !== true || !['applied', 'already_applied'].includes(body?.status)) {
      await settle(row.shopId, lead.id, { attempts });
      return 'keep'; // a 200 that is not our answer (a captive portal's page)
    }
    await settle(row.shopId, lead.id, { status: 'sent', attempts });
    return 'sent';
  } catch (error) {
    const status = error?.response?.status;
    if (status === 401) return 'paused';
    if ((status === 403 || status === 422) && isOurApi(error.response)) {
      await settle(row.shopId, lead.id, { status: 'refused', attempts });
      return 'refused';
    }
    await settle(row.shopId, lead.id, { attempts });
    return 'keep';
  }
}

async function sendAll() {
  const rows = await previewStore().list();
  const statuses = {};
  for (const row of rows) if (row.lead?.status) statuses[shopKey(row.shopId)] = row.lead.status;
  leadStatuses.value = statuses;

  const pending = rows.filter((row) => row.lead?.status === 'pending').sort((a, b) => String(a.lead.at).localeCompare(String(b.lead.at)));
  knownEmpty = pending.length === 0;
  for (const row of pending) {
    const outcome = await sendOne(row);
    if (outcome === 'paused') {
      pauseForAuth();
      return;
    }
    if (outcome === 'keep') {
      failures += 1;
      const delay = BACKOFF_MS[Math.min(failures, BACKOFF_MS.length) - 1];
      nextAttemptAt = Date.now() + delay;
      clearTimeout(retryTimer);
      retryTimer = setTimeout(() => sendPendingLeads({ reason: 'retry' }), delay);
      return;
    }
  }
  failures = 0;
  nextAttemptAt = 0;
  knownEmpty = true;
}

async function withLock(work) {
  const locks = typeof navigator === 'undefined' ? null : navigator.locks;
  if (typeof locks?.request !== 'function') return work();
  return locks.request(LEADS_LOCK, { ifAvailable: true }, (lock) => (lock ? work() : undefined));
}

/**
 * Send what waits (the tap queue's `flush` calls this after every attempt).
 * Like the queue: the tick and the login coming back wait out the backoff; the
 * rest try at once. Never rejects.
 */
export function sendPendingLeads({ reason = 'tap' } = {}) {
  sendPendingExit();
  if (sending) return sending;
  if (authPaused.value) return Promise.resolve();
  if ((reason === 'tick' || reason === 'resume') && Date.now() < nextAttemptAt) return Promise.resolve();
  // A tap or a tick with nothing waiting does not read every copy again.
  if (knownEmpty && (reason === 'tap' || reason === 'tick' || reason === 'again')) return Promise.resolve();
  sending = withLock(sendAll)
    .catch((error) => console.warn('[collector/previewLeads] sending the leads failed', error))
    .finally(() => {
      sending = null;
    });
  return sending;
}

/** Tests only. */
export function resetPreviewLeadsForTests() {
  clearTimeout(retryTimer);
  retryTimer = null;
  sending = null;
  sendingExit = null;
  failures = 0;
  nextAttemptAt = 0;
  knownEmpty = false;
  leadStatuses.value = {};
}
